import { Link } from "react-router-dom";
import { UserPlus, Check } from "lucide-react";

/**
 * One-click "Add as Partner" link used on operational rows (Gate Events,
 * Wash Station) where the only customer/agent reference is a free-text name.
 *
 * Props:
 *   • name        – the customer/agent string (e.g. "BUNGE")
 *   • role        – default partner role to assign ("customer" or "carrier")
 *   • canEdit     – hide the "+" affordance for operators (read-only)
 *   • knownCodes  – Set<string> of UPPERCASED partner codes already on file.
 *                   Pass it from the page so we don't hit /api/partners per row.
 *
 * Behaviour:
 *   • If a partner with a matching code already exists → muted check-icon
 *     link to the Partners page (no "Add" button needed).
 *   • Otherwise → small "+" deep-link to
 *     /partners?new=1&legal_name=<name>&code=<code>&role=<role>.
 *
 * Skips render when name is empty.
 */
export default function AddAsPartnerButton({ name, role = "customer", canEdit = true, knownCodes, size = "xs" }) {
  const trimmed = (name || "").trim();
  if (!trimmed) return null;

  // Normalize to a partner code: uppercase, replace spaces with _, strip
  // anything that isn't alphanumeric or underscore, then cap at 20 chars.
  // Mirrors backend _norm_code semantics so the lookup matches.
  const code = trimmed
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 20);
  if (!code) return null;
  const exists = knownCodes && knownCodes.has(code);

  if (exists) {
    return (
      <Link
        to="/partners"
        title={`Linked to partner ${code}`}
        data-testid={`partner-linked-${code}`}
        className="inline-flex items-center text-[#99D420] hover:text-[#006E9E]"
        onClick={(e) => e.stopPropagation()}
      >
        <Check className={size === "xs" ? "w-3 h-3" : "w-4 h-4"} />
      </Link>
    );
  }

  if (!canEdit) return null;

  const href =
    `/partners?new=1` +
    `&legal_name=${encodeURIComponent(trimmed)}` +
    `&code=${encodeURIComponent(code)}` +
    `&role=${role}`;

  return (
    <Link
      to={href}
      title={`Add "${trimmed}" as a partner`}
      data-testid={`add-partner-${code}`}
      className="inline-flex items-center text-gray-400 hover:text-[#006E9E] transition-colors"
      onClick={(e) => e.stopPropagation()}
    >
      <UserPlus className={size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5"} />
    </Link>
  );
}
